import { Command } from "commander";
import { readFileSync, writeFileSync, mkdirSync, existsSync } from "node:fs";
import { resolve, dirname } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import {
  parseSpec,
  estimateAll,
  generateSensitivity,
  generateReport,
  generateRegistry,
  loadData,
} from "@fathom/core";
import { syncEstimate, logConvexStatus } from "../convex-client.js";
import { getProjectName } from "../project-name.js";
import { fatalError } from "../ux/errors.js";

export const analyzeCommand = new Command("analyze")
  .description("Analyze a spec and generate estimate report + project scaffolding")
  .argument("<spec>", "Path to the spec markdown file")
  .option("-p, --project <name>", "Project name")
  .option("-o, --output <dir>", "Output directory for reports", ".")
  .option("--json", "Output report as JSON")
  .action(async (specPath: string, options: { project?: string; output: string; json?: boolean }) => {
    const fullPath = resolve(specPath);

    if (!existsSync(fullPath)) {
      fatalError("Spec not found", fullPath + " does not exist", "Check the file path and try again");
    }

    const content = readFileSync(fullPath, "utf-8");
    const data = loadData();

    // Parse
    console.log(chalk.dim("Parsing spec..."));
    const spec = parseSpec(content);
    const projectName = options.project ?? spec.metadata.project ?? getProjectName();

    if (spec.features.length === 0) {
      fatalError("No features in spec", "Spec must use ### Feature: <name> headings", "Add feature headings to your spec file");
    }

    console.log(chalk.green(`Found ${spec.features.length} features in "${projectName}"`));

    // Estimate
    console.log(chalk.dim("Estimating tokens..."));
    const estimates = estimateAll(spec.features, data);
    const sensitivity = generateSensitivity(estimates, data.models);
    const report = generateReport(projectName, estimates, sensitivity, content);

    // Display results
    if (options.json) {
      console.log(JSON.stringify(report, null, 2));
    } else {
      displayReport(report);
    }

    // Generate registry
    const registry = generateRegistry(projectName, spec.features, estimates);

    // Write outputs
    const outDir = resolve(options.output);
    const reportPath = resolve(outDir, `fathom-estimate-${projectName}.json`);
    writeFileSync(reportPath, JSON.stringify(report, null, 2));
    console.log(chalk.dim(`\nReport saved: ${reportPath}`));

    const registryPath = resolve(outDir, `fathom-registry-${projectName}.json`);
    writeFileSync(registryPath, JSON.stringify(registry, null, 2));
    console.log(chalk.dim(`Registry saved: ${registryPath}`));

    // Scaffold .fathom/ if --project is set
    if (options.project) {
      scaffoldProject(projectName, registry, outDir);
    }

    // Sync to Convex
    const synced = await syncEstimate({
      projectName,
      specHash: report.specHash,
      features: estimates.map((e) => ({
        id: e.featureId,
        name: e.featureName,
        category: e.category,
        complexity: e.complexity,
        estimatedInputTokens: e.withOverhead.input,
        estimatedOutputTokens: e.withOverhead.output,
        estimatedCost: e.estimatedCost,
        recommendedModel: e.recommendedModel,
        taskTypes: e.taskTypes,
        overheadBreakdown: e.overheadBreakdown,
      })),
      totalEstimate: {
        tokens: report.totals.tokens,
        cost: report.totals.cost,
        optimistic: report.sensitivity.find((s) => s.name === "Optimistic")?.totalCost ?? report.totals.cost * 0.75,
        pessimistic: report.sensitivity.find((s) => s.name === "Pessimistic")?.totalCost ?? report.totals.cost * 1.5,
        allFrontier: report.sensitivity.find((s) => s.name === "All-Frontier")?.totalCost ?? report.totals.cost * 2,
      },
    });
    logConvexStatus(synced);
  });

function displayReport(report: ReturnType<typeof generateReport>) {
  console.log(chalk.bold(`\nFathom — Estimate Report: ${report.project}`));
  console.log(chalk.dim("─".repeat(50)));

  const table = new Table({
    head: [
      chalk.white("Feature"),
      chalk.white("Size"),
      chalk.white("Model"),
      chalk.white("Tokens"),
      chalk.white("Cost"),
    ],
    colAligns: ["left", "center", "left", "right", "right"],
  });

  for (const f of report.features) {
    table.push([
      f.featureName,
      f.complexity,
      f.recommendedModel.replace("claude-", "").replace("-4-6", "").replace("-4-5", ""),
      f.withOverhead.total.toLocaleString(),
      `$${f.estimatedCost.toFixed(2)}`,
    ]);
  }

  console.log(table.toString());

  console.log(
    chalk.bold(`\nTotal: ${report.totals.tokens.toLocaleString()} tokens — $${report.totals.cost.toFixed(2)}`),
  );

  console.log(chalk.dim("\nSensitivity:"));
  for (const s of report.sensitivity) {
    const label = s.name.padEnd(22);
    console.log(
      `  ${label} ${s.totalTokens.toLocaleString().padStart(10)} tokens  $${s.totalCost.toFixed(2)}`,
    );
  }
}

function scaffoldProject(
  projectName: string,
  registry: ReturnType<typeof generateRegistry>,
  baseDir: string,
) {
  const fathomDir = resolve(baseDir, ".fathom");
  mkdirSync(fathomDir, { recursive: true });

  const registryPath = resolve(fathomDir, "registry.json");
  writeFileSync(registryPath, JSON.stringify(registry, null, 2));

  const configPath = resolve(fathomDir, "config.json");
  writeFileSync(
    configPath,
    JSON.stringify({ project: projectName, createdAt: new Date().toISOString() }, null, 2),
  );

  console.log(chalk.green(`\nScaffolded .fathom/ for "${projectName}"`));
  console.log(chalk.dim(`  registry: ${registryPath}`));
  console.log(chalk.dim(`  config:   ${configPath}`));
}
